import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createUseStyles, useTheme } from "react-jss";
import ReactTooltip from "react-tooltip";
import MinusWhiteIcon from "../assets/icons/minus/PNG-minus-white.png";
import MinusBlackIcon from "../assets/icons/minus/PNG-minus-black.png";
import PlusWhiteIcon from "../assets/icons/plus/PNG-plus-white.png";
import PlusBlackIcon from "../assets/icons/plus/PNG-plus-black.png";
import CategoryInfoIcon from "../assets/icons/category-info/PNG-category-info.png";

const counterButton = {
  fontFamily: "DustinSans",
  border: "none !important",
  paddingLeft: "16px !important",
  paddingRight: "16px !important",
  size: "14px !important",
  borderRadius: "2px !important",
  touchAction: "manipulation !important",
  display: "flex !important",
  alignItems: "center !important",
};

const useStyles = createUseStyles({
  deviceCategory: {
    width: "100%",
    margin: 8,
    minWidth: 276,
    maxWidth: 312,
    minHeight: 152,
    left: 0,
    top: 0,
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
    borderRadius: 4,
    display: "flex",
    flexBasis: "21%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    "@media (max-width: 615px)": {
      flexDirection: "row",
      minHeight: "auto",
      minWidth: "-webkit-fill-available",
    },
  },
  categoryInfoIcon: {
    alignSelf: "flex-end",
    width: "20px",
    height: "20px",
    position: "absolute",
    transform: "translate(8px,-8px)",
    "@media (max-width: 615px)": {
      transform: "translate(35px,-30px)",
    },
  },
  deviceLogo: {
    marginTop: 8,
    height: 32,
    objectFit: "contain",
    "@media (max-width: 615px)": {
      marginTop: 0,
    },
  },
  categoryName: {
    fontSize: 12,
    color: "#4F4F4F",
  },
  deviceCounter: {
    display: "flex",
    alignItems: "stretch",
    justifyContent: "space-between",
    width: "100%",
    height: 40,
    borderRadius: 2,
    "@media (max-width: 615px)": {
      width: "140px",
    },
  },
  counterAmount: {
    size: 14,
    width: "100%",
    textAlign: "center",
    margin: 0,
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  counterAmountInputField: {
    alignSelf: "center !important",
    maxWidth: "100px !important",
    margin: "auto !important",
    fontSize: "14px !important",
    fontFamily: "DustinSans",
    height: "24px !important",
    textAlign: "center !important",
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      "-webkit-appearance": "none !important",
      "-moz-appearance": "none !important",
      margin: "0px !important",
    },
    "&[type=number]": {
      "-webkit-appearance": "textfield !important",
      "-moz-appearance": "textfield !important",
    },
    "@media (max-width: 615px)": {
      maxWidth: "30px !important",
    },
  },
  minusButtonZero: {
    ...counterButton,
    backgroundColor: ({ theme }) => theme.dustinLight,
    color: "black !important",
    "&:hover": {
      cursor: "pointer !important",
      backgroundColor: ({ theme }) => theme.dustinSemiLight,
    },
  },
  plusButtonZero: {
    ...counterButton,
    backgroundColor: ({ theme }) => theme.dustinLight,
    color: "black !important",
    "&:hover": {
      backgroundColor: ({ theme }) => theme.dustinSemiLight,
      cursor: "pointer !important",
    },
  },
  minusButton: {
    ...counterButton,
    backgroundColor: ({ theme }) => theme.dustinDark,
    color: "white",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: ({ theme }) => theme.dustinDarker,
    },
  },
  plusButton: {
    ...counterButton,
    backgroundColor: ({ theme }) => theme.dustinDark,
    color: "white",
    "&:hover": {
      backgroundColor: ({ theme }) => theme.dustinDarker,
      cursor: "pointer",
    },
  },
  counterButtonImage: {
    objectFit: "contain !important"
  }
});

const DeviceCategory = ({
  categoryName,
  icon,
  amount,
  onAmountChange,
  categoryInfo,
}) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [displayInputField, setDisplayInputField] = useState(false);

  const inputFieldRef = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (
        inputFieldRef.current &&
        !inputFieldRef.current.contains(event.target)
      ) {
        setDisplayInputField(false);
      }
    };
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return (
    <div className={classes.deviceCategory}>
      <ReactTooltip place="top" effect="float" multiline />
      {categoryInfo && (
        <img
          src={CategoryInfoIcon}
          alt={"info"}
          className={classes.categoryInfoIcon}
          data-tip={categoryInfo}
        />
      )}
      <img
        width={32}
        height={32}
        src={icon}
        alt={categoryName}
        className={classes.deviceLogo}
      />
      <div className={classes.categoryName}>{categoryName}</div>
      <div
        className={classes.deviceCounter}
        style={
          amount > 0
            ? {
                backgroundColor: theme.dustinDark,
                color: "white",
              }
            : {
                backgroundColor: theme.dustinLight,
                color: "black",
              }
        }
      >
        <button
          className={amount > 0 ? classes.minusButton : classes.minusButtonZero}
          onClick={() => (amount > 0 ? onAmountChange(amount - 1) : {})}
        >
          <img
            width={10}
            height={10}
            src={amount > 0 ? MinusWhiteIcon : MinusBlackIcon}
            alt={"-"}
            className={classes.counterButtonImage}
          />
        </button>

        {displayInputField ? (
          <input
            ref={inputFieldRef}
            className={classes.counterAmountInputField}
            type="number"
            min="0"
            step="1"
            value={amount}
            onChange={(e) => {
              // eslint-disable-next-line radix
              const val = parseInt(e.target.value);
              onAmountChange(isNaN(val) ? 0 : val);
            }}
          />
        ) : (
          <div
            className={classes.counterAmount}
            onClick={() => setDisplayInputField(true)}
          >
            {amount}
          </div>
        )}

        <button
          className={amount > 0 ? classes.plusButton : classes.plusButtonZero}
          onClick={() => onAmountChange(amount + 1)}
        >
          <img
            width={10}
            height={10}
            src={amount > 0 ? PlusWhiteIcon : PlusBlackIcon}
            alt={"+"}
            className={classes.counterButtonImage}
          />
        </button>
      </div>
    </div>
  );
};

DeviceCategory.propTypes = {
  categoryName: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  categoryInfo: PropTypes.string,
};

export default DeviceCategory;



// WEBPACK FOOTER //
// ./src/components/DeviceCategory.js